import { computed, defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";

import { AssetsService } from "../../../../../services/assets-service";
import { ToastrService } from "../../../../../services/toastr.service";
import { TaskService } from "../../../../../services/task.service";
import { DownloadService } from "../../../../../services/download.service";
import { ParentPermissionEnum, PermissionActionEnum } from "../../../../../enums/enums";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import { IBlobResponse } from "../../../../../resources-vue/vue-interfaces/i-hospice-client";
import {
    IDeleteHHHCTaskAttachmentRequest,
    IGetHHHCTaskAttachmentRequest,
    IHHHCTaskAsset,
    ISaveHHHCTaskAttachmentRequest,
    ISaveHHHCTaskAttachmentResponse
} from "../../../../../resources-vue/vue-interfaces/i-task";
import { IPromptOptions } from "../../../../../resources-vue/vue-dialogs/prompt/prompt";

import Prompt from "../../../../../resources-vue/vue-dialogs/prompt/prompt.vue";

export default defineComponent({
    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    props: {
        visitDetails: { type: Object },
        isPlanner: { type: Boolean, default: false }
    },

    data() {
        return {
            uploadedFiles: [] as File[],
            taskAttchments: [] as IHHHCTaskAsset[],
            attachmentToDelete: null as string,
            uploading: false,
            loadingCount: 3,
            isDeleteInProgress: false,
            addPatientDocumentsPermission: false,
            dialogRef: null,
            deletePatientDocumentsPermission: false,
            editPatientDocumentsPermission: false,
            loadingAttachments: false,
            _taskService: null as TaskService,
            _assetsService: null as AssetsService,
            _downloadService: null as DownloadService,
            _permissionManager: null as PermissionManager,
            _toastrService: null as ToastrService
        };
    },

    created() {
        this._assetsService = Container.instance.get(AssetsService);
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._downloadService = Container.instance.get(DownloadService);
    },

    async mounted() {
        this.addPatientDocumentsPermission = this._permissionManager.checkPermission(
            ParentPermissionEnum.referrals,
            PermissionActionEnum.canAdd,
            this.visitDetails?.AgencyId
        );
        this.deletePatientDocumentsPermission = this._permissionManager.checkPermission(
            ParentPermissionEnum.referrals,
            PermissionActionEnum.canDelete,
            this.visitDetails?.AgencyId
        );
        this.editPatientDocumentsPermission = this._permissionManager.checkPermission(
            ParentPermissionEnum.referrals,
            PermissionActionEnum.canEdit,
            this.visitDetails?.AgencyId
        );
        await this.loadAttchments();
    },

    methods: {
        async requestDelete(document: IHHHCTaskAsset) {
            this.attachmentToDelete = document.AssetId;
            try {
                const promptOptions: IPromptOptions = {
                    message: "Are you sure you want to delete?",
                    okText: "Yes",
                    cancelText: "No"
                };

                this.dialogRef = this.$dialog.open(Prompt, {
                    props: {
                        modal: true,
                        contentStyle: {
                            overflowX: "hidden"
                        },
                        showHeader: false
                    },
                    data: {
                        promptOptions
                    },
                    onClose: async (confirm) => {
                        if (confirm.data === "success") {
                            await this.deleteSelectedDocument();
                        }
                    }
                });
            } catch (error) {
                this._toastrService.error({
                    title: `Error - Document`,
                    message: `There was a problem while deleting the document. Please try again.`
                });
            }
        },

        async deleteSelectedDocument() {
            try {
                const payload: IDeleteHHHCTaskAttachmentRequest = {
                    taskId: this.visitDetails?.Id,
                    accountId: this.visitDetails?.AgencyId,
                    assetId: this.attachmentToDelete,
                    application: this.visitDetails?.Application,
                    patientId: this.visitDetails?.PatientId,
                    episodeId: this.visitDetails?.EpisodeId,
                    isForUser: false
                };
                this.isDeleteInProgress = true;
                await this._taskService.deleteHHHCTaskAttachment(payload);
                this.isDeleteInProgress = false;
                this.loadAttchments();
                this.attachmentToDelete = null;
                this._toastrService.success({
                    message: `The document has been successfully deleted.`,
                    title: `Document Deleted`
                });
            } catch (error) {
                console.error(error);
                this.isDeleteInProgress = false;
                this._toastrService.error({
                    title: `Delete Document Failed`,
                    message: `Delete document failed, please contact axxess if issue still persists.`
                });
            }
        },

        extractAssetDownloadPayload(document: IHHHCTaskAsset) {
            return {
                assetId: document.AssetId,
                application: this.visitDetails?.Application,
                agencyId: this.visitDetails?.AgencyId
            };
        },

        async downloadDocuments(document: IHHHCTaskAsset) {
            const payload = this.extractAssetDownloadPayload(document);
            await this._assetsService
                .downloadPatientDocuments(payload)
                .then((response: any) => {
                    this._downloadService.downloadFile(response, response.type);
                    this._toastrService.success({
                        message: `The requested document is downloaded.`
                    });
                })
                .catch((error: any) => {
                    console.error(error);
                    this._toastrService.error({
                        title: `Downloading Document Failed`,
                        message: `Downloading document failed, please contact axxess if issue still persists.`
                    });
                });
        },

        async openSelectedDocument(document: IHHHCTaskAsset) {
            const payload = this.extractAssetDownloadPayload(document);
            return await this._assetsService
                .downloadPatientChart(payload)
                .then((response: IBlobResponse) => {
                    const url = window.URL.createObjectURL(response.body as Blob);
                    window.open(url, "_blank");
                    this._toastrService.success({
                        message: `The requested document is downloaded.`
                    });
                })
                .catch((error: any) => {
                    console.error(error);
                    this._toastrService.error({
                        title: `Opening Document Failed`,
                        message: `Opening document failed, please contact axxess if issue still persists.`
                    });
                });
        },

        async loadAttchments() {
            this.loadingAttachments = true;
            try {
                const payload: IGetHHHCTaskAttachmentRequest = {
                    application: this.visitDetails?.Application,
                    agencyId: this.visitDetails?.AgencyId,
                    taskId: this.visitDetails?.Id,
                    episodeId: this.visitDetails?.EpisodeId,
                    patientId: this.visitDetails?.PatientId
                };
                const response = await this._taskService.getHHHCTaskAttachment(payload);
                this.taskAttchments = response.TaskAssets;
            } catch (error) {
                console.error(error);
                this._toastrService.error({
                    title: `Error`,
                    message: `Error loading attachments, please contact axxess if issue still persists.`
                });
            } finally {
                this.loadingAttachments = false;
            }
        },

        uploadFilesChanged(event: any) {
            this.uploadedFiles.push(event.target.files[0]);
        },

        async saveAttachments() {
            if (this.uploadedFiles.length) {
                try {
                    this.uploading = true;
                    const formData = new FormData();
                    this.uploadedFiles.forEach((file, index) => {
                        formData.append(`file${index}`, file);
                    });
                    const payload: ISaveHHHCTaskAttachmentRequest = {
                        AgencyId: this.visitDetails?.AgencyId,
                        Application: this.visitDetails?.Application,
                        TaskId: this.visitDetails?.Id,
                        PatientId: this.visitDetails?.PatientId,
                        EpisodeId: this.visitDetails?.EpisodeId
                    };
                    formData.append("request", JSON.stringify(payload));
                    const response: ISaveHHHCTaskAttachmentResponse =
                        await this._taskService.saveHHHCTaskAttachment(formData);
                    this.uploadedFiles = [];
                    if (response.IsSuccessful) {
                        (this.$refs.file1 as HTMLInputElement).value = "";
                        this._toastrService.success({
                            title: "Attachment(s) Saved",
                            message: "The attachment(s) have been saved successfully."
                        });
                        this.loadAttchments();
                    } else {
                        this._toastrService.error({
                            title: `Error`,
                            message: response.ErrorMessage
                                ? response.ErrorMessage
                                : response.Message
                                  ? response.Message
                                  : "Error uploading file, please try again."
                        });
                    }
                } catch (error) {
                    console.error(error);
                    this._toastrService.error({
                        title: `Error`,
                        message: "Error uploading file, please try again."
                    });
                } finally {
                    this.uploading = false;
                }
            } else {
                this.uploading = false;
                this._toastrService.error({
                    title: `Error`,
                    message: `Please fill required Fields`
                });
            }
        }
    }
});
